import { getFormattedDate, getFormattedDateTime, getFormattedTime } from '@app/utils/date'
import { Grid, Typography } from '@mui/material'
import { FormattedMessage } from 'react-intl'
import SimpleTooltip from '../ui/SimpleTooltip'
import { DateTime } from 'luxon'
import { useSelector } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles'

const useStyles = makeStyles(() => ({
  loginText: {
    textAlign: 'center',
    maxWidth: 500,
  },
  entryDeadlineCancelText: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
}))

interface Props {
  noLongerAcceptingEntries: boolean
  cancellationsClosed: boolean
  cancellationsWillClose: boolean
  entriesSetButNotOpen: boolean
  entriesAndCancellationsOpen: boolean
  entryOpenDateTime: DateTime
  entryEndDateTime: DateTime
  cancelByDateTime: DateTime
}

const SignUpText = ({
  noLongerAcceptingEntries,
  cancellationsClosed,
  cancellationsWillClose,
  entriesSetButNotOpen,
  entriesAndCancellationsOpen,
  entryOpenDateTime,
  entryEndDateTime,
  cancelByDateTime,
}: Props) => {
  const classes = useStyles()
  const site = useSelector((store: StoreState) => store.tournamentReducer.tournamentSite)

  if (site.onlineEntriesEnabled && !site.registerAndCancelTimeFramesEnabled) {
    return null
  }

  if (noLongerAcceptingEntries && cancellationsClosed) {
    return (
      <Grid item>
        <Typography variant={'body1'} className={classes.entryDeadlineCancelText}>
          <FormattedMessage id="strings.noLongerAcceptingEntries" />
          <br />
          <FormattedMessage
            id="strings.cancellationsClosed"
            values={{
              closeDate: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedDate(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
              closeTime: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedTime(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
            }}
          />
        </Typography>
      </Grid>
    )
  }

  if (noLongerAcceptingEntries && cancellationsWillClose) {
    return (
      <Grid item>
        <Typography variant={'body1'} className={classes.entryDeadlineCancelText}>
          <FormattedMessage id="strings.noLongerAcceptingEntries" />
          <br />
          <FormattedMessage
            id="strings.cancellationsWillClose"
            values={{
              closeDate: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedDate(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
              closeTime: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedTime(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
            }}
          />
        </Typography>
      </Grid>
    )
  }

  if (entriesSetButNotOpen) {
    return (
      <Grid item>
        <Typography variant={'body1'} className={classes.entryDeadlineCancelText}>
          <FormattedMessage
            id="strings.entriesSetButNotOpen"
            values={{
              openDateTime: (
                <SimpleTooltip title={`${entryOpenDateTime.zoneName} (${entryOpenDateTime.offsetNameShort})`}>
                  <span>{getFormattedDateTime(entryOpenDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
              closeDateTime: (
                <SimpleTooltip title={`${entryEndDateTime.zoneName} (${entryEndDateTime.offsetNameShort})`}>
                  <span>{getFormattedDateTime(entryEndDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
            }}
          />
          <br />
          <FormattedMessage
            id="strings.cancellationsWillClose"
            values={{
              closeDate: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedDate(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
              closeTime: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedTime(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
            }}
          />
        </Typography>
      </Grid>
    )
  }

  if (
    site.registerAndCancelTimeFramesEnabled &&
    DateTime.now() >= entryOpenDateTime &&
    DateTime.now() <= entryEndDateTime &&
    cancellationsClosed
  ) {
    return (
      <Grid item>
        <Typography variant={'body1'} className={classes.entryDeadlineCancelText}>
          <FormattedMessage
            id="strings.entriesClose"
            values={{
              closeDate: (
                <SimpleTooltip title={`${entryEndDateTime.zoneName} (${entryEndDateTime.offsetNameShort})`}>
                  <span>{getFormattedDate(entryEndDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
              closeTime: (
                <SimpleTooltip title={`${entryEndDateTime.zoneName} (${entryEndDateTime.offsetNameShort})`}>
                  <span>{getFormattedTime(entryEndDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
            }}
          />
          <br />
          <FormattedMessage
            id="strings.cancellationsClosed"
            values={{
              closeDate: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedDate(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
              closeTime: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedTime(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
            }}
          />
        </Typography>
      </Grid>
    )
  }

  if (site.registerAndCancelTimeFramesEnabled && entriesAndCancellationsOpen) {
    return (
      <Grid item>
        <Typography variant={'body1'} className={classes.entryDeadlineCancelText}>
          <FormattedMessage
            id="strings.entriesClose"
            values={{
              closeDate: (
                <SimpleTooltip title={`${entryEndDateTime.zoneName} (${entryEndDateTime.offsetNameShort})`}>
                  <span>{getFormattedDate(entryEndDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
              closeTime: (
                <SimpleTooltip title={`${entryEndDateTime.zoneName} (${entryEndDateTime.offsetNameShort})`}>
                  <span>{getFormattedTime(entryEndDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
            }}
          />
          <br />
          <FormattedMessage
            id="strings.cancellationsWillClose"
            values={{
              closeDate: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedDate(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
              closeTime: (
                <SimpleTooltip title={`${cancelByDateTime.zoneName} (${cancelByDateTime.offsetNameShort})`}>
                  <span>{getFormattedTime(cancelByDateTime, site.units)}</span>
                </SimpleTooltip>
              ),
            }}
          />
        </Typography>
      </Grid>
    )
  }

  return null
}

export default SignUpText
